import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  FaPhone, FaEnvelope, FaGlobe, FaBuilding, FaBriefcase, FaMapMarkerAlt, FaShareAlt,
  FaFacebook, FaInstagram, FaTwitter, FaLinkedin, FaYoutube, 
  FaTiktok, FaSnapchat, FaWhatsapp, FaTelegram, FaPinterest, FaGithub,
  FaFileAlt, FaThumbsUp, FaSpinner, FaStar, FaFilePdf, FaEye,
  FaUserPlus
} from 'react-icons/fa';
import FlipbookViewer from '../components/FlipbookViewer';
import './VCardView.css';

// Social icon mapping
const getSocialIcon = (platform) => {
  const icons = {
    facebook: FaFacebook,
    instagram: FaInstagram,
    twitter: FaTwitter,
    linkedin: FaLinkedin,
    youtube: FaYoutube,
    tiktok: FaTiktok,
    snapchat: FaSnapchat,
    whatsapp: FaWhatsapp,
    telegram: FaTelegram,
    pinterest: FaPinterest,
    github: FaGithub,
  };
  return icons[platform.toLowerCase()] || FaGlobe;
};

// Helper function to format WhatsApp URL for direct messaging
const formatWhatsAppUrl = (url) => {
  if (!url) return url;
  
  // If it's already a WhatsApp URL (wa.me or api.whatsapp.com), return as-is
  if (url.includes('wa.me/') || url.includes('api.whatsapp.com')) {
    return url;
  }
  
  // Extract phone number from URL or use URL as phone number
  let phoneNumber = url;
  
  // Remove common URL prefixes
  phoneNumber = phoneNumber.replace(/^(https?:\/\/)?(www\.)?/, '');
  phoneNumber = phoneNumber.replace(/^wa\.me\//, '');
  phoneNumber = phoneNumber.replace(/^api\.whatsapp\.com\/send\?phone=/, '');
  
  // Remove all non-digit characters except +
  phoneNumber = phoneNumber.replace(/[^\d+]/g, '');
  
  // Remove leading + if present (wa.me doesn't need it)
  phoneNumber = phoneNumber.replace(/^\+/, '');
  
  // If we have a valid phone number, format as WhatsApp URL
  if (phoneNumber && /^\d{7,15}$/.test(phoneNumber)) {
    return `https://wa.me/${phoneNumber}`;
  }
  
  // If it doesn't look like a phone number, return original URL
  return url;
};

// Helper function to create WhatsApp inquiry URL with pre-filled message
const createWhatsAppInquiryUrl = (whatsappUrl, serviceTitle) => {
  if (!whatsappUrl) return null;
  
  // Format the WhatsApp URL first
  const formattedUrl = formatWhatsAppUrl(whatsappUrl);
  
  // Create inquiry message
  const message = `Hi! I'm interested in learning more about "${serviceTitle}". Could you provide more details?`;
  
  // Encode the message for URL
  const encodedMessage = encodeURIComponent(message);
  
  // Add message parameter to WhatsApp URL
  if (formattedUrl.includes('wa.me/')) {
    return `${formattedUrl}?text=${encodedMessage}`;
  } else if (formattedUrl.includes('api.whatsapp.com')) {
    return `${formattedUrl}&text=${encodedMessage}`;
  }
  
  return formattedUrl;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function to get initials from full name
const getInitials = (fullName) => {
  if (!fullName) return '?';
  const nameParts = fullName.trim().split(/\s+/);
  if (nameParts.length >= 2) {
    // First letter of first name + first letter of last name
    return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
  } else if (nameParts.length === 1) {
    // If only one name, use first two letters
    return nameParts[0].substring(0, 2).toUpperCase();
  }
  return fullName.charAt(0).toUpperCase();
};

// Helper function to get a color based on name (for consistent avatar colors)
const getAvatarColor = (name) => {
  if (!name) return '#667eea';
  const colors = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', // Purple
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', // Pink
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', // Blue
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', // Green
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', // Orange
    'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', // Teal
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', // Light
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', // Rose
  ];
  // Use name to consistently pick a color
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Service Card Component
const ServiceCard = ({ service, whatsappUrl, ownerIsPremium }) => {
  const hasWhatsApp = !!whatsappUrl;
  const showInquiryButton = hasWhatsApp && ownerIsPremium;
  
  return (
    <div className={`service-card ${service.featured ? 'featured' : ''}`}>
      {service.image && (
        <div className="service-card-image">
          <img 
            src={service.image.startsWith('http') ? service.image : `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}${service.image}`} 
            alt={service.title}
            onError={(e) => e.target.style.display = 'none'}
          />
        </div>
      )}
      <div className="service-card-content">
        <h4>
          {service.title}
          {service.featured && <FaStar className="featured-badge" />}
        </h4>
        {service.description && <p>{service.description}</p>}
        {service.price && <div className="service-price">{service.price}</div>}
        {showInquiryButton && (
          <a
            href={createWhatsAppInquiryUrl(whatsappUrl, service.title)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-inquiry"
          >
            <FaWhatsapp /> Inquiry
          </a>
        )}
      </div>
    </div>
  );
};

const VCardView = () => {
  const { uniqueId } = useParams();
  const [vcard, setVcard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSplash, setShowSplash] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  // All sections are always expanded (no toggle functionality)
  const [viewingBrochure, setViewingBrochure] = useState(null);
  const [activeTab, setActiveTab] = useState('about'); // 'about', 'contact', or 'services' - default to about

  useEffect(() => {
    fetchVCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueId]);

  useEffect(() => {
    if (vcard && vcard.welcomeScreenImage) {
      // Show splash screen for 5 seconds
      setShowSplash(true);
      const timer = setTimeout(() => {
        setShowSplash(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [vcard]);

  const fetchVCard = async () => {
    try {
      const response = await axios.get(`${API_URL}/vcards/public/${uniqueId}`);
      const vcardData = response.data;
      
      // Ensure image URLs are properly formatted
      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
      if (vcardData.profileImage && !vcardData.profileImage.startsWith('http') && !vcardData.profileImage.startsWith('data:')) {
        vcardData.profileImage = `${baseUrl}${vcardData.profileImage}`;
      }
      if (vcardData.welcomeScreenImage && !vcardData.welcomeScreenImage.startsWith('http') && !vcardData.welcomeScreenImage.startsWith('data:')) {
        vcardData.welcomeScreenImage = `${baseUrl}${vcardData.welcomeScreenImage}`;
      }
      
      setVcard(vcardData);
    } catch (error) {
      console.error('Error fetching vCard:', error);
    } finally {
      setLoading(false);
    }
  };

  // All sections are expanded by default, toggle function removed

  const handleAddToContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/vcards/${vcard._id}/vcard`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${vcard.fullName.replace(/\s+/g, '_')}.vcf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading vCard:', error);
      alert('Failed to download contact. Please try again.');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${vcard.fullName}'s Contact`,
          text: `Check out ${vcard.fullName}'s contact information`,
          url: window.location.href,
        });
      } catch (error) {
        console.error('Error sharing:', error);
        // If share is cancelled or fails, fall through to clipboard copy
        copyToClipboard(window.location.href);
      }
    } else {
      // Fallback: copy to clipboard
      copyToClipboard(window.location.href);
    }
  };

  const copyToClipboard = (text) => {
    // Try modern Clipboard API first (requires HTTPS or localhost)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          alert('Link copied to clipboard!');
        })
        .catch((error) => {
          console.error('Clipboard API failed:', error);
          // Fallback to older method
          fallbackCopyToClipboard(text);
        });
    } else {
      // Fallback for browsers without Clipboard API (HTTP sites, older browsers)
      fallbackCopyToClipboard(text);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        alert('Link copied to clipboard!');
      } else {
        // Last resort: show the URL for manual copying
        prompt('Copy this link:', text);
      }
    } catch (error) {
      console.error('Fallback copy failed:', error);
      // Last resort: show the URL for manual copying
      prompt('Copy this link:', text);
    }
  };

  if (loading) {
    return <div className="vcard-view-loading">Loading...</div>;
  }

  if (!vcard) {
    return <div className="vcard-view-error">vCard not found</div>;
  }

  // Show splash screen for 5 seconds if welcome image exists
  if (showSplash && vcard.welcomeScreenImage) {
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
    const welcomeImageUrl = vcard.welcomeScreenImage.startsWith('http') || vcard.welcomeScreenImage.startsWith('data:')
      ? vcard.welcomeScreenImage 
      : `${baseUrl}${vcard.welcomeScreenImage}`;
    return (
      <div 
        className="vcard-splash-screen"
        style={{
          backgroundColor: vcard.backgroundColor || '#F5F5F5'
        }}
      >
        <div className="splash-content">
          <img 
            src={welcomeImageUrl} 
            alt="Welcome" 
            className="splash-image"
            onError={(e) => {
              console.error('Welcome image failed to load:', e.target.src);
              // If image fails to load, hide splash and show content
              setShowSplash(false);
            }}
          />
          <div className="splash-loading">
            <FaSpinner className="spinner-icon" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="vcard-view-wrapper"
      style={{
        backgroundColor: vcard.backgroundColor || '#F5F5F5',
      }}
    >
      <div
        className="vcard-view"
        style={{
          background: `linear-gradient(135deg, ${vcard.primaryColor} 0%, ${vcard.secondaryColor} 100%)`,
        }}
      >
        <div className="vcard-view-container">
        <div className="vcard-header">
          {vcard.profileImage && !imageLoadError ? (
            <img 
              src={vcard.profileImage.startsWith('http') || vcard.profileImage.startsWith('data:') 
                ? vcard.profileImage 
                : (() => {
                    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
                    return `${baseUrl}${vcard.profileImage}`;
                  })()} 
              alt={vcard.fullName} 
              className="vcard-profile-image"
              onError={(e) => {
                console.error('Profile image failed to load:', e.target.src);
                setImageLoadError(true);
                e.target.style.display = 'none';
              }}
            />
          ) : null}
          {(!vcard.profileImage || imageLoadError) && (
            <div 
              className="vcard-profile-placeholder"
              style={{ background: getAvatarColor(vcard.fullName) }}
            >
              {getInitials(vcard.fullName)}
            </div>
          )}
          <h1 className="vcard-name">{vcard.fullName}</h1>
          <div className="vcard-action-buttons">
            <button className="vcard-add-contact-btn" onClick={handleAddToContacts}>
              <FaUserPlus /> Add contact
            </button>
            <button className="vcard-share-btn" onClick={handleShare}>
              <FaShareAlt /> Share
            </button>
          </div>
        </div>

        <div className="vcard-content">
          {/* Tabs */}
          <div className="vcard-tabs">
            <button 
              className={`vcard-tab ${activeTab === 'about' ? 'active' : ''}`}
              onClick={() => setActiveTab('about')}
            >
              About
            </button>
            <button 
              className={`vcard-tab ${activeTab === 'contact' ? 'active' : ''}`}
              onClick={() => setActiveTab('contact')}
            >
              Contact
            </button>
            <button 
              className={`vcard-tab ${activeTab === 'services' ? 'active' : ''}`}
              onClick={() => setActiveTab('services')}
            >
              Services & Resources
            </button>
          </div>

          {/* About Tab Content */}
          {activeTab === 'about' && (
            <div className="vcard-tab-content">
              {/* Business Section */}
              {(vcard.companyName || vcard.title || (vcard.addressType === 'manual' && (vcard.address?.street || vcard.address?.city))) && (
                <div className="vcard-section">
                  <div className="vcard-section-header">
                    <FaBriefcase className="section-icon" />
                    <span>Business</span>
                  </div>
                  <div className="vcard-section-content">
                    {vcard.companyName && (
                      <div className="company-item">
                        <FaBuilding /> {vcard.companyName}
                      </div>
                    )}
                    {vcard.title && (
                      <div className="company-item">
                        <FaBriefcase /> {vcard.title}
                      </div>
                    )}
                    {(vcard.addressType === 'manual' && (vcard.address?.street || vcard.address?.city)) && (
                      <div className="company-item">
                        <FaMapMarkerAlt /> {
                          [
                            vcard.address?.street,
                            vcard.address?.city,
                            vcard.address?.state,
                            vcard.address?.postalCode,
                            vcard.address?.country
                          ].filter(Boolean).join(', ')
                        }
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Summary Section */}
              {vcard.summary && (
                <div className="vcard-section">
                  <div className="vcard-section-header">
                    <FaFileAlt className="section-icon" />
                    <span>Summary</span>
                  </div>
                  <div className="vcard-section-content">
                    <p style={{ textAlign: 'justify' }}>{vcard.summary}</p>
                  </div>
                </div>
              )}

              {/* Social Media Section */}
              {vcard.socialMedia?.length > 0 && (
                <div className="vcard-section">
                  <div className="vcard-section-header">
                    <FaThumbsUp className="section-icon" />
                    <span>Social media ({vcard.socialMedia.length})</span>
                  </div>
                  <div className="vcard-section-content">
                    {vcard.socialMedia.map((sm, idx) => {
                      const IconComponent = getSocialIcon(sm.platform);
                      // Format platform name nicely (capitalize first letter)
                      const platformName = sm.platform.charAt(0).toUpperCase() + sm.platform.slice(1);
                      
                      // Handle WhatsApp links specially - convert to WhatsApp messaging URL
                      const isWhatsApp = sm.platform.toLowerCase() === 'whatsapp';
                      const socialUrl = isWhatsApp ? formatWhatsAppUrl(sm.url) : sm.url;
                      
                      return (
                        <a
                          key={idx}
                          href={socialUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="social-item"
                        >
                          <IconComponent className="social-icon-item" />
                          <span>{platformName}</span>
                          <span>▶</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contact Tab Content */}
          {activeTab === 'contact' && (
            <div className="vcard-tab-content">
              {/* Phone Numbers Section */}
              {(vcard.phoneNumbers?.length > 0 || vcard.alternativePhoneNumbers?.length > 0) && (
                <div className="vcard-section">
                  <div className="vcard-section-header">
                    <FaPhone className="section-icon" />
                    <span>Phone Numbers</span>
                  </div>
                  <div className="vcard-section-content">
                    {vcard.phoneNumbers?.map((phone, idx) => (
                      <a key={idx} href={`tel:${phone}`} className="contact-item">
                        <FaPhone /> {phone}
                      </a>
                    ))}
                    {vcard.alternativePhoneNumbers?.map((phone, idx) => (
                      <a key={idx} href={`tel:${phone}`} className="contact-item">
                        <FaPhone /> {phone}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Email Addresses Section */}
              {vcard.emails?.length > 0 && (
                <div className="vcard-section">
                  <div className="vcard-section-header">
                    <FaEnvelope className="section-icon" />
                    <span>Email Addresses</span>
                  </div>
                  <div className="vcard-section-content">
                    {vcard.emails?.map((email, idx) => (
                      <a key={idx} href={`mailto:${email}`} className="contact-item">
                        <FaEnvelope /> {email}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Website Section */}
              {vcard.website && (
                <div className="vcard-section">
                  <div className="vcard-section-header">
                    <FaGlobe className="section-icon" />
                    <span>Website</span>
                  </div>
                  <div className="vcard-section-content">
                    <a href={vcard.website} target="_blank" rel="noopener noreferrer" className="contact-item">
                      <FaGlobe /> {vcard.website}
                    </a>
                  </div>
                </div>
              )}

              {/* Quick Actions Section */}
              {(vcard.phoneNumbers?.length > 0 || vcard.alternativePhoneNumbers?.length > 0 || vcard.emails?.length > 0 || vcard.socialMedia?.some(sm => sm.platform.toLowerCase() === 'whatsapp')) && (
                <div className="vcard-section">
                  <div className="vcard-section-header">
                    <FaPhone className="section-icon" />
                    <span>Quick Actions</span>
                  </div>
                  <div className="vcard-section-content">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {(vcard.phoneNumbers?.length > 0 || vcard.alternativePhoneNumbers?.length > 0) && (
                        <>
                          {vcard.phoneNumbers?.map((phone, idx) => (
                            <a key={idx} href={`tel:${phone}`} className="btn btn-primary" style={{ textAlign: 'center', textDecoration: 'none' }}>
                              <FaPhone /> Call {phone}
                            </a>
                          ))}
                          {vcard.alternativePhoneNumbers?.map((phone, idx) => (
                            <a key={idx} href={`tel:${phone}`} className="btn btn-primary" style={{ textAlign: 'center', textDecoration: 'none' }}>
                              <FaPhone /> Call {phone}
                            </a>
                          ))}
                        </>
                      )}
                      {/* WhatsApp button - only show if WhatsApp social link exists */}
                      {vcard.socialMedia?.some(sm => sm.platform.toLowerCase() === 'whatsapp') && (() => {
                        const whatsappLink = vcard.socialMedia.find(sm => sm.platform.toLowerCase() === 'whatsapp');
                        const whatsappUrl = whatsappLink ? formatWhatsAppUrl(whatsappLink.url) : null;
                        return whatsappUrl ? (
                          <a 
                            href={whatsappUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="btn" 
                            style={{ 
                              textAlign: 'center', 
                              textDecoration: 'none',
                              backgroundColor: '#25D366',
                              color: 'white',
                              border: 'none'
                            }}
                          >
                            <FaWhatsapp /> WhatsApp
                          </a>
                        ) : null;
                      })()}
                      {vcard.emails?.length > 0 && (
                        <a 
                          href={`mailto:${vcard.emails[0]}`} 
                          className="btn btn-secondary" 
                          style={{ textAlign: 'center', textDecoration: 'none' }}
                        >
                          <FaEnvelope /> Email {vcard.emails[0]}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Services & Resources Tab Content */}
          {activeTab === 'services' && (
            <div className="vcard-tab-content">
              {/* Services Showcase Section */}
              {vcard.services?.length > 0 && (
                <div className="vcard-section">
                  <div className="vcard-section-header">
                    <FaStar className="section-icon" />
                    <span>Product & Services ({vcard.services.length})</span>
                  </div>
                  <div className="vcard-section-content">
                    <div className="services-grid">
                      {vcard.services.map((service, idx) => {
                        // Get WhatsApp URL if available
                        const whatsappLink = vcard.socialMedia?.find(sm => sm.platform.toLowerCase() === 'whatsapp');
                        const whatsappUrl = whatsappLink ? whatsappLink.url : null;
                        const ownerIsPremium = vcard.ownerIsPremium || false;
                        
                        return (
                          <ServiceCard
                            key={idx}
                            service={service}
                            whatsappUrl={whatsappUrl}
                            ownerIsPremium={ownerIsPremium}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Brochures/Flipbook Section */}
              {vcard.brochures?.length > 0 && (
                <div className="vcard-section">
                  <div className="vcard-section-header">
                    <FaFilePdf className="section-icon" />
                    <span>Brochures ({vcard.brochures.length})</span>
                  </div>
                  <div className="vcard-section-content">
                    <div className="brochures-grid">
                      {vcard.brochures.map((brochure, idx) => {
                        return (
                          <div key={idx} className="brochure-card">
                            {brochure.thumbnail ? (
                              <div className="brochure-card-thumbnail">
                                <img 
                                  src={brochure.thumbnail.startsWith('http') 
                                    ? brochure.thumbnail 
                                    : (() => {
                                        const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
                                        return `${baseUrl}${brochure.thumbnail.startsWith('/') ? brochure.thumbnail : `/${brochure.thumbnail}`}`;
                                      })()} 
                                  alt={brochure.title}
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              </div>
                            ) : (
                              <div className="brochure-card-thumbnail placeholder">
                                <FaFilePdf />
                              </div>
                            )}
                            <div className="brochure-card-content">
                              <h4>{brochure.title}</h4>
                              {brochure.description && <p>{brochure.description}</p>}
                              <div className="brochure-meta">
                                <span className="brochure-type">{brochure.type.toUpperCase()}</span>
                                {brochure.pageCount > 0 && (
                                  <span className="brochure-pages">{brochure.pageCount} pages</span>
                                )}
                              </div>
                              {brochure.pages && brochure.pages.length > 0 && (
                                <button
                                  onClick={() => setViewingBrochure(brochure)}
                                  className="btn-view-brochure"
                                >
                                  <FaEye /> View Flipbook
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
      </div>

      {viewingBrochure && (
        <FlipbookViewer
          brochure={viewingBrochure}
          onClose={() => setViewingBrochure(null)}
        />
      )}
    </div>
  );
};

export default VCardView;

